import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, RefreshCw, MessageCircle, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "iqmize_stats_key";

const SOURCE_LABELS = {
  hero: "Hero — Get the Book",
  nav: "Header CTA",
  "nav-mobile": "Mobile Menu CTA",
  floating: "Floating Button",
  book: "Book Section",
  "coaching-group": "Coaching — Group",
  "coaching-individual": "Coaching — Individual",
  "final-cta-book": "Final CTA — Book",
  "final-cta-group": "Final CTA — Group",
  "final-cta-individual": "Final CTA — Individual",
  footer: "Footer",
};

const NUMBER_LABELS = {
  "917034618014": "70346 18014",
  "918590566268": "85905 66268",
};

const Stats = () => {
  const [passcode, setPasscode] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(() => Boolean(sessionStorage.getItem(STORAGE_KEY)));
  const [input, setInput] = useState("");
  const [authError, setAuthError] = useState(false);
  const [rows, setRows] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async (key) => {
    if (!key) return;
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/events/summary`, { params: { key } });
      setRows(res.data);
      const enq = await axios.get(`${API}/enquiries`, { params: { key } });
      setEnquiries(enq.data);
      const pr = await axios.get(`${API}/payment-proofs`, { params: { key } });
      setProofs(pr.data);
      setAuthed(true);
      setAuthError(false);
      sessionStorage.setItem(STORAGE_KEY, key);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthed(false);
        setAuthError(true);
        sessionStorage.removeItem(STORAGE_KEY);
      } else {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (passcode) load(passcode);
  }, [passcode, load]);

  const submitPasscode = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setPasscode(input.trim());
  };

  if (!authed) {
    return (
      <div data-testid="stats-gate" className="min-h-screen bg-background flex items-center justify-center px-5">
        <form
          onSubmit={submitPasscode}
          className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_20px_40px_rgb(0,0,0,0.08)] text-center"
        >
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand text-white">
            <Lock size={24} strokeWidth={2} />
          </div>
          <h1 className="mt-5 text-xl font-bold text-slate-900">IQMIZE Click Stats</h1>
          <p className="mt-1.5 text-sm text-slate-500">Enter your passcode to continue.</p>
          <input
            type="password"
            required
            data-testid="stats-passcode-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Passcode"
            className="mt-6 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
          {authError && (
            <p data-testid="stats-auth-error" className="mt-3 text-sm font-semibold text-red-600">
              Incorrect passcode. Try again.
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="stats-passcode-submit"
            className="mt-5 w-full rounded-full bg-brand px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-brand-dark disabled:opacity-60"
          >
            {loading ? "Checking..." : "Unlock"}
          </button>
          <a href="/" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:text-brand-dark">
            <ArrowLeft size={15} /> Back to site
          </a>
        </form>
      </div>
    );
  }

  const clicks = rows.filter((r) => r.event === "whatsapp_click");
  const total = clicks.reduce((s, r) => s + r.count, 0);

  const bySource = {};
  const byNumber = {};
  for (const r of clicks) {
    const src = r.source || "unknown";
    bySource[src] = (bySource[src] || 0) + r.count;
    if (r.number) byNumber[r.number] = (byNumber[r.number] || 0) + r.count;
  }
  const sourceRows = Object.entries(bySource).sort((a, b) => b[1] - a[1]);
  const maxSource = sourceRows.length ? sourceRows[0][1] : 1;

  return (
    <div data-testid="stats-page" className="min-h-screen bg-background">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-5 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/iqmize-logo.png" alt="IQMIZE" className="h-10 w-auto" />
            <h1 className="text-lg font-bold text-slate-900">Click Stats</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => load(passcode)}
              data-testid="stats-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition-colors duration-200 hover:bg-brand-light"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <a href="/" data-testid="stats-back-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:text-brand-dark">
              <ArrowLeft size={15} /> Back to site
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-10 space-y-8">
        {error && (
          <p data-testid="stats-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600">
            Could not load stats. Please try again.
          </p>
        )}

        <div data-testid="stats-total-card" className="rounded-3xl bg-inavy p-8 text-white flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-igold">Total WhatsApp Taps</p>
            <p className="mt-2 text-5xl font-extrabold" data-testid="stats-total-count">{total}</p>
          </div>
          <MessageCircle size={48} className="text-white/20" />
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-7" data-testid="stats-by-source">
          <h2 className="text-base font-bold text-slate-900">Taps per button</h2>
          {sourceRows.length === 0 && !loading && (
            <p className="mt-4 text-sm text-slate-500">No clicks recorded yet.</p>
          )}
          <div className="mt-5 space-y-4">
            {sourceRows.map(([src, count]) => (
              <div key={src} data-testid={`stats-source-${src}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-700">{SOURCE_LABELS[src] || src}</span>
                  <span className="font-extrabold text-inavy">{count}</span>
                </div>
                <div className="mt-1.5 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-brand transition-[width] duration-500" style={{ width: `${(count / maxSource) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-7" data-testid="stats-by-number">
          <h2 className="text-base font-bold text-slate-900">Floating button — number split</h2>
          <div className="mt-5 grid grid-cols-2 gap-4">
            {Object.entries(NUMBER_LABELS).map(([num, label]) => (
              <div key={num} className="rounded-2xl bg-brand-light p-5 text-center" data-testid={`stats-number-${num}`}>
                <p className="text-2xl font-extrabold text-inavy">{byNumber[num] || 0}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-7" data-testid="stats-enquiries">
          <h2 className="text-base font-bold text-slate-900">Recent enquiries</h2>
          {enquiries.length === 0 && !loading && (
            <p className="mt-4 text-sm text-slate-500">No enquiries yet.</p>
          )}
          <div className="mt-5 space-y-3">
            {enquiries.map((e) => (
              <div
                key={e.id}
                data-testid={`stats-enquiry-${e.id}`}
                className="rounded-2xl border border-slate-100 bg-brand-light/60 px-5 py-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-extrabold text-slate-900">{e.name}</p>
                  <span className="rounded-full bg-white px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-brand border border-slate-200">
                    {e.interest}
                  </span>
                </div>
                <p className="mt-1 text-sm font-semibold text-slate-600">{e.phone}</p>
                {e.message && <p className="mt-1.5 text-sm text-slate-500">"{e.message}"</p>}
                <p className="mt-2 text-[11px] font-semibold text-slate-400">
                  {new Date(e.timestamp).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-7" data-testid="stats-proofs">
          <h2 className="text-base font-bold text-slate-900">Payment proofs</h2>
          <p className="mt-1 text-xs text-slate-500">Buyers who submitted a UPI transaction ID after paying.</p>
          {proofs.length === 0 && !loading && (
            <p className="mt-4 text-sm text-slate-500">No payment proofs yet.</p>
          )}
          <div className="mt-5 space-y-3">
            {proofs.map((p) => (
              <div
                key={p.id}
                data-testid={`stats-proof-${p.id}`}
                className="rounded-2xl border border-slate-100 bg-brand-light/60 px-5 py-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-extrabold text-slate-900">{p.name}</p>
                  <span className="rounded-full bg-igold/20 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-slate-800 border border-igold/40">
                    ₹49 paid
                  </span>
                </div>
                <p className="mt-1 text-sm font-semibold text-slate-600">{p.phone}</p>
                <p className="mt-1.5 text-sm font-bold text-inavy">Txn ID: {p.txn_id}</p>
                <p className="mt-2 text-[11px] font-semibold text-slate-400">
                  {new Date(p.timestamp).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Stats;
